import React, { useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { model } from '@whizz/api-client';
import type { ToggleModalProps } from '../../components/modal/types';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { EndDateType, StartDateType } from '../../util/types';
import { Button, Loader } from '../../components';
import { GLOBAL_ICONS } from '../../util/vars';
import { generatePDF } from '../../helper/pdfHelper';
import { useIdentity } from '@whizz/react-identity';
import { saveAsCsv } from '../../util/reactFunc';
import { DateTime } from 'luxon';
import { getDateTimeObj } from '../../util/dates';

export type SchoolDownloadModalProps = Pick<ToggleModalProps, 'toggle'> & { startDate: StartDateType, endDate: EndDateType };

const SchoolDownloadModal = ({ toggle, startDate, endDate }: SchoolDownloadModalProps): JSX.Element => {

  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const [ isFileDownloading, setIsFileDownloading ] = useState<'pdf' | 'csv' | false>(false);

  const elementsIds =
     [
       'settings-container',
       'assessment-info-container', // TBD remove if unnecessary
       'table-container',
       'maths-age-delta-container',
       'school-progressions-container',
       'school-quality-learning-time-container',
       'school-maths-age-improvements-container',
       'school-progress-consistency-container',
       'school-student-status-container',
       'initial-assessment-completed-container',
       'school-topic-progress-container',
     ];

  const togglePrinting = (print: boolean) => {
    const tag = document.getElementsByTagName('html')[0];
    if (print) {
      tag?.classList.add('printing');
    } else {
      tag?.classList.remove('printing');
    }
  };

  const handleCurrentView = () => {
    togglePrinting(true);
    setIsFileDownloading('pdf');
    const styleLinks: NodeListOf<HTMLLinkElement> = document.querySelectorAll('link[href^=\'https://wchat\']') ;
    const chatButton = document.getElementById('fc_frame');
    const chatFrame = document.getElementById('fc_push_frame');
    if (null != chatButton) chatButton.style.display = 'none';
    if (null != chatFrame) chatFrame.style.display = 'none'; // Prevents default div styles to be applied while generating the PDF
    styleLinks.forEach((stylesheet) => stylesheet.disabled = true);
    setTimeout(() => {
      generatePDF('report', elementsIds).then(() => {
        togglePrinting(false);
        setIsFileDownloading(false);
        toggle(false);
      });
    }, 250);
  };
  const handleStudentData = () => {
    const user = (me as Teacher);
    if (user.school_id) {
      setIsFileDownloading('csv');
      model.CsvReports.schoolAllStudents(user.school_id, locale, startDate, endDate ?? DateTime.now())
        .then((data: string) => {
          saveAsCsv(data, translate('pages.REPORTS.DOWNLOAD.FILE_NAMES.student', { date: getDateTimeObj(DateTime.now(), locale?.id).toFormat('yyyy_LL_dd') }).toUpperCase());
        })
        .finally(() => {
          setIsFileDownloading(false);
          toggle(false);
        });
    }
  };
  const handleClassData = () => {
    const user = (me as Teacher);
    if (user.school_id) {
      setIsFileDownloading('csv');
      model.CsvReports.schoolClasses(user.school_id, locale, startDate, endDate ?? DateTime.now())
        .then((data: string) => {
          saveAsCsv(data, translate('pages.REPORTS.DOWNLOAD.FILE_NAMES.school', { date: getDateTimeObj(DateTime.now(), locale?.id).toFormat('yyyy_LL_dd') }).toUpperCase());
        })
        .finally(() => {
          setIsFileDownloading(false);
          toggle(false);
        });
    }
  };

  if (isFileDownloading) {
    return (
      <Loader
        label={translate(`pages.REPORTS.DOWNLOAD.MODAL.loader_${isFileDownloading}`)}
      />
    );
  }

  return (
    <>
      <div className={'flex items-center justify-center container'}>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleCurrentView}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.current')}
        </Button>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleClassData}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.class')}
        </Button>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleStudentData}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.all')}
        </Button>
      </div>
    </>
  );
};

SchoolDownloadModal.displayName = 'SchoolDownloadModal';

export default SchoolDownloadModal;
